import { Square, Volume2 } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useApiClient } from "@/api/ApiContext";
import {
  synthesizeSpeech,
  usePreviewSpeechConfigQuery,
} from "@/api/previewSpeech";
import { useCapability } from "@/auth/SessionContext";
import {
  READ_ALOUD_KEY,
  clipForTenantVoice,
  detectSpeechTag,
  missingVoiceNote,
  readFlag,
  speakText,
  speechTag,
  stopSpeaking,
  synthesisOf,
  writeFlag,
} from "@/lib/labSpeech";
import { IconButton } from "@/ui/IconButton";

const BROWSER_LABEL = "Browser voice (tenant speech unavailable)";

/**
 * Reading Liv's replies aloud: with the run's tenant voice when the tenant
 * offers one, else (or when a call fails) with the browser's own speech engine.
 * Text only passes through: nothing is stored or logged, and the audio is
 * played from memory.
 */

type LabVoiceValue = {
  available: boolean;
  /** Which voice reads: the tenant's, or the browser's. */
  mode: "tenant" | "browser";
  /** Which voice is in use, or null while that is still being asked. */
  label: string | null;
  autoRead: boolean;
  setAutoRead: (on: boolean) => void;
  /** The id of the line being spoken now, or null. */
  speakingId: string | null;
  /** One line about a missing voice, or null. */
  note: string | null;
  play: (id: string, text: string) => void;
  stop: () => void;
  /** Called once per reply as it shows; speaks it only when reading aloud is on. */
  announce: (id: string, text: string, isNew: boolean) => void;
};

const LabVoiceContext = createContext<LabVoiceValue | null>(null);

export function useLabVoice(): LabVoiceValue | null {
  return useContext(LabVoiceContext);
}

export function LabVoiceProvider({
  children,
  projectId,
  previewRunRef,
  language,
}: {
  children: ReactNode;
  projectId: string;
  previewRunRef: string;
  /** The session's language (`he` or `en`), passed to the tenant voice. */
  language?: string;
}) {
  const client = useApiClient();
  const inspect = useCapability("preview.inspect");
  const create = useCapability("preview.create");
  const config = usePreviewSpeechConfigQuery(projectId, previewRunRef, {
    enabled: inspect.allowed,
  });
  const [tenantFailed, setTenantFailed] = useState(false);
  const tenantUsable =
    !tenantFailed && create.allowed && config.data?.ttsAvailable === true;
  const mode: "tenant" | "browser" = tenantUsable ? "tenant" : "browser";
  const label = tenantUsable
    ? `Tenant voice: Azure ${config.data?.voice ?? config.data?.language ?? ""}`.trim()
    : config.isLoading
      ? null
      : BROWSER_LABEL;
  const available = tenantUsable || synthesisOf() !== null;
  const [autoReadChoice, setAutoReadState] = useState(() =>
    readFlag(READ_ALOUD_KEY, false),
  );
  const autoRead = available && autoReadChoice;
  const [speakingId, setSpeakingId] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const seen = useRef(new Set<string>());
  const autoReadRef = useRef(autoRead);
  useEffect(() => {
    autoReadRef.current = autoRead;
  }, [autoRead]);
  const audio = useRef<HTMLAudioElement | null>(null);
  const audioUrl = useRef<string | null>(null);
  const inFlight = useRef<AbortController | null>(null);

  // Ends the tenant voice: the request in flight and the audio playing.
  const stopTenant = useCallback(() => {
    inFlight.current?.abort();
    inFlight.current = null;
    if (audio.current !== null) {
      audio.current.onended = null;
      audio.current.onerror = null;
      audio.current.pause();
    }
    if (audioUrl.current !== null) {
      URL.revokeObjectURL(audioUrl.current);
      audioUrl.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    stopTenant();
    stopSpeaking();
    setSpeakingId(null);
  }, [stopTenant]);

  const playBrowser = useCallback((id: string, text: string) => {
    const tag = detectSpeechTag(text, "en");
    setSpeakingId(id);
    const result = speakText(text, tag, () =>
      setSpeakingId((current) => (current === id ? null : current)),
    );
    if (result === null) {
      setSpeakingId(null);
      return;
    }
    setNote(result.matched ? null : missingVoiceNote(tag));
  }, []);

  const playTenant = useCallback(
    async (id: string, text: string) => {
      const request = new AbortController();
      inFlight.current = request;
      setSpeakingId(id);
      try {
        const blob = await synthesizeSpeech(
          client,
          projectId,
          previewRunRef,
          {
            text: clipForTenantVoice(text),
            ...(language === undefined
              ? {}
              : { language: speechTag(language) }),
          },
          request.signal,
        );
        if (request.signal.aborted) {
          return;
        }
        const url = URL.createObjectURL(blob);
        audioUrl.current = url;
        const element = (audio.current ??= new Audio());
        const done = () => {
          if (audioUrl.current === url) {
            stopTenant();
          }
          setSpeakingId((current) => (current === id ? null : current));
        };
        element.onended = done;
        element.onerror = done;
        element.src = url;
        await element.play();
        setNote(null);
      } catch {
        if (request.signal.aborted) {
          return;
        }
        // The tenant voice failed (service, network or autoplay policy): the
        // browser reads this line, and keeps reading for the session.
        stopTenant();
        setTenantFailed(true);
        playBrowser(id, text);
      }
    },
    [client, language, playBrowser, previewRunRef, projectId, stopTenant],
  );

  const play = useCallback(
    (id: string, text: string) => {
      stop();
      if (tenantUsable) {
        void playTenant(id, text);
      } else {
        playBrowser(id, text);
      }
    },
    [playBrowser, playTenant, stop, tenantUsable],
  );

  const announce = useCallback(
    (id: string, text: string, isNew: boolean) => {
      if (seen.current.has(id)) {
        return;
      }
      seen.current.add(id);
      if (isNew && autoReadRef.current) {
        play(id, text);
      }
    },
    [play],
  );

  const setAutoRead = useCallback(
    (on: boolean) => {
      setAutoReadState(on);
      writeFlag(READ_ALOUD_KEY, on);
      if (!on) {
        stop();
      }
    },
    [stop],
  );

  // Leaving the page stops the voice.
  useEffect(
    () => () => {
      stopTenant();
      stopSpeaking();
    },
    [stopTenant],
  );

  const value = useMemo(
    () => ({
      available,
      mode,
      label,
      autoRead,
      setAutoRead,
      speakingId,
      note,
      play,
      stop,
      announce,
    }),
    [
      available,
      mode,
      label,
      autoRead,
      setAutoRead,
      speakingId,
      note,
      play,
      stop,
      announce,
    ],
  );
  return (
    <LabVoiceContext.Provider value={value}>
      {children}
    </LabVoiceContext.Provider>
  );
}

/** The header switch: read each new Liv reply aloud. */
export function SpeakerToggle() {
  const voice = useLabVoice();
  if (voice === null) {
    return null;
  }
  return (
    <div className="flex flex-col items-end gap-0.5">
      <label
        className="flex items-center gap-1.5 text-sm"
        title={
          voice.available
            ? undefined
            : "Reading aloud needs a browser with speech synthesis or a tenant voice"
        }
      >
        <input
          type="checkbox"
          checked={voice.autoRead}
          disabled={!voice.available}
          onChange={(event) => voice.setAutoRead(event.target.checked)}
        />
        <Volume2 aria-hidden="true" className="size-4" />
        Read Liv&apos;s replies aloud
      </label>
      {voice.label !== null ? (
        <p role="status" className="text-xs text-muted-foreground">
          {voice.label}
        </p>
      ) : null}
      {voice.mode === "browser" && voice.autoRead && voice.note !== null ? (
        <p role="status" className="text-xs text-muted-foreground">
          {voice.note}
        </p>
      ) : null}
    </div>
  );
}

/** A small play / stop button on one Liv bubble. */
export function PlayLineButton({ id, text }: { id: string; text: string }) {
  const voice = useLabVoice();
  if (voice === null || !voice.available || text.trim() === "") {
    return null;
  }
  const speaking = voice.speakingId === id;
  return (
    <IconButton
      type="button"
      label={speaking ? "Stop reading" : "Read this reply aloud"}
      showTooltip={false}
      title={speaking ? "Stop reading" : "Read this reply aloud"}
      aria-pressed={speaking}
      className="size-7"
      icon={
        speaking ? (
          <Square className="size-3.5" />
        ) : (
          <Volume2 className="size-3.5" />
        )
      }
      onClick={() => (speaking ? voice.stop() : voice.play(id, text))}
    />
  );
}
